import { Box, Stack } from '@chakra-ui/react';
import type React from 'react';
import FaceliftText from '../FaceliftText';

export interface RadioItem {
	label: string;
	value: string;
}

export interface FaceliftRadioGroupProps {
	value?: string | null;
	onValueChange: (value: string) => void;
	items: RadioItem[];
	gap?: string | number;
	direction?: 'row' | 'column';
	error?: string;
	allowDeselect?: boolean;
}

const FaceliftRadioGroup: React.FC<FaceliftRadioGroupProps> = ({
	value,
	onValueChange,
	items,
	gap = 6,
	direction = 'row',
	error,
	allowDeselect = false,
}) => {
	const RadioButton: React.FC<{
		checked: boolean;
		onChange: () => void;
		label: string;
	}> = ({ checked, onChange, label }) => (
		<button
			type="button"
			role="radio"
			aria-checked={checked}
			onClick={onChange}
			style={{
				width: '100%',
				cursor: 'pointer',
				display: 'flex',
				justifyContent: 'flex-start',
				alignItems: 'center',
				gap: '8px',
				background: 'transparent',
				border: 'none',
				padding: 0,
				textAlign: 'left',
			}}
		>
			<Box
				w="15px"
				h="15px"
				minW="15px"
				minH="15px"
				flexShrink={0}
				borderRadius="full"
				border="2px solid"
				borderColor={checked ? 'brand.success' : 'gray.400'}
				display="flex"
				alignItems="center"
				justifyContent="center"
				transition="all 0.2s"
			>
				{checked && (
					<Box
						w="8px"
						h="8px"
						borderRadius="full"
						bg="brand.success"
					/>
				)}
			</Box>
			<FaceliftText
				fontSize="sm"
				flex="1"
				textAlign="left"
				lineHeight="1.35"
				whiteSpace="normal"
				wordBreak="break-word"
			>
				{label}
			</FaceliftText>
		</button>
	);

	return (
		<Box>
			<Stack gap={gap} direction={direction} align="stretch">
				{items.map((item) => (
					<RadioButton
						key={item.value}
						checked={value === item.value}
						onChange={() =>
							onValueChange(allowDeselect && value === item.value ? '' : item.value)
						}
						label={item.label}
					/>
				))}
			</Stack>
			{error && (
				<FaceliftText color="red.500" fontSize="sm" mt={1}>
					{error}
				</FaceliftText>
			)}
		</Box>
	);
};

export default FaceliftRadioGroup;
